/**
 * PM-WF-01 — the project's Workflow panel on the project detail page
 * (projects/v1@2.25.0). The `AiModelsPanel` pattern: reads the project detail,
 * shows the current value, and edits it through its own request
 * (`PATCH /projects/{projectId}/workflow`).
 *
 * What it says:
 *   - the name of the workflow VERSION the project pins (M3C-03/M3C-04: the
 *     name from workflows/v1, the kind words from its injection node's served facts); a
 *     project that pins no version (created before its tenant owned a
 *     workflow) shows its legacy key's label;
 *   - "Workflow not set" when the project has none — the pilot then picks one
 *     at intake (DI-D9);
 *   - and, always next to the editor and after a save, that a change applies to
 *     NEW data acquisitions only — an acquisition keeps the workflow it was
 *     created with (DI-D9 c3).
 *
 * Roles: `project_manager` / `tenant_admin` (and `platform_admin`) edit — the
 * matrix permission `project.create`, the gate the BE enforces on the PATCH;
 * everyone else reads. A member (shared-in) project's workflow is its owning
 * company's setting.
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useCanEditResourceFor } from "@aeriosiq/ui";
import { useToast } from "../../account/ToastContext.js";
import { useWorkflows } from "../../workflows/api/workflows.js";
import {
  useProjectDetailQuery,
  useUpdateProjectWorkflowMutation,
} from "../useProjects.js";
import {
  WorkflowSelect,
  projectWorkflowLabel,
  projectWorkflowMeaning,
  workflowErrorMessage,
} from "../WorkflowSelect.js";

export const NEW_ACQUISITIONS_ONLY_NOTE =
  "Applies to new data acquisitions; existing ones keep their workflow.";

export interface WorkflowPanelProps {
  projectId: string;
  /** True when the project is the caller's OWN. Defaults true. */
  isOwnerProject?: boolean;
}

export function WorkflowPanel({
  projectId,
  isOwnerProject = true,
}: WorkflowPanelProps): ReactElement {
  const { toast } = useToast();
  const canEditRole = useCanEditResourceFor("project.create");
  const canEdit = isOwnerProject && canEditRole;
  const detail = useProjectDetailQuery(isOwnerProject ? projectId : null);
  const workflows = useWorkflows();
  const update = useUpdateProjectWorkflowMutation(projectId);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState(false);

  if (!isOwnerProject) {
    return (
      <Alert severity="info" variant="outlined">
        The workflow of a shared project is set by its owning company.
      </Alert>
    );
  }

  // M3C-04 — the version is the project's workflow; the legacy key is shown
  // only for a project that pins none (created before its tenant's seed).
  const currentVersion = detail.data?.workflowVersionId ?? null;
  const legacyKey = detail.data?.workflow ?? null;
  const current = currentVersion ?? legacyKey;
  const meaning = projectWorkflowMeaning(currentVersion, workflows.data);

  function startEdit(): void {
    setDraft(currentVersion ?? "");
    setSaveError(null);
    setSavedNote(false);
    setEditing(true);
  }

  function onSave(): void {
    if (!draft) return;
    setSaveError(null);
    update.mutate(draft, {
      onSuccess: () => {
        setEditing(false);
        setSavedNote(true);
        toast("Workflow saved.", "success");
      },
      onError: (err) => {
        setSaveError(workflowErrorMessage(err));
      },
    });
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="workflow-panel">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">Workflow</Typography>
        {detail.isSuccess && current === null ? (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label="Workflow not set"
          />
        ) : null}
      </Stack>

      {detail.isPending ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress aria-label="Loading the workflow" />
        </Box>
      ) : detail.isError ? (
        <Alert severity="error">
          Couldn&apos;t load the project&apos;s workflow.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {current === null ? (
            <Typography variant="body2" color="text.secondary">
              This project defines no workflow, so the pilot chooses one for
              each data acquisition at intake.
            </Typography>
          ) : (
            <Box>
              <Typography variant="body1" data-testid="workflow-panel-label">
                {projectWorkflowLabel(
                  currentVersion,
                  workflows.data,
                  detail.data.workflowLabel,
                  legacyKey,
                )}
              </Typography>
              {meaning ? (
                <Typography variant="body2" color="text.secondary">
                  {meaning}
                </Typography>
              ) : null}
            </Box>
          )}

          {savedNote ? (
            <Alert severity="success" variant="outlined">
              Workflow saved. {NEW_ACQUISITIONS_ONLY_NOTE}
            </Alert>
          ) : null}

          {editing ? (
            <Stack spacing={1.5}>
              <WorkflowSelect
                value={draft}
                onChange={setDraft}
                required
                id={`project-workflow-${projectId}`}
              />
              <Typography variant="body2" color="text.secondary">
                {NEW_ACQUISITIONS_ONLY_NOTE}
              </Typography>
              {saveError ? <Alert severity="error">{saveError}</Alert> : null}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  onClick={() => {
                    setEditing(false);
                    setSaveError(null);
                  }}
                  disabled={update.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={onSave}
                  disabled={
                    !draft || draft === currentVersion || update.isPending
                  }
                >
                  {update.isPending ? "Saving…" : "Save workflow"}
                </Button>
              </Stack>
            </Stack>
          ) : canEdit ? (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="outlined" onClick={startEdit}>
                {current === null ? "Set workflow" : "Change workflow"}
              </Button>
            </Box>
          ) : null}
        </Stack>
      )}
    </Paper>
  );
}
